// src/modules/app-views/crm/pages/Boards/components/StickyBoard.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useRef, useState } from 'react';
import { Empty, Input, Tooltip, theme } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import sticksService from 'modules/app-views/crm/sticksService';
import StickyNote from './StickyNote';
import StickyNoteModal from './StickyNoteModal';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const StickyBoard = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { onReady } = props;
  const { token } = theme.useToken();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const initialLoadDone = useRef(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [notes, setNotes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Load notes from API on mount
  useEffect(() => {
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;

    (async () => {
      try {
        const data = await sticksService.fetchAll();
        setNotes(data);
      } catch {
        // ignore fetch errors on initial load
      } finally {
        onReady?.();
      }
    })();
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleAddNote = useCallback(() => {
    setEditingNote(null);
    setShowModal(true);
  }, []);

  const handleEditNote = useCallback(note => {
    setEditingNote(note);
    setShowModal(true);
  }, []);

  const handleDeleteNote = useCallback(id => {
    appService.modal({
      flag: 'confirm',
      title: 'delete',
      content: 'delete_note_confirm',
      okText: 'yes',
      cancelText: 'no',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await sticksService.remove(id);
          setNotes(prev => prev.filter(n => (n._id || n.uid) !== id));
        } catch {
          // ignore delete errors
        }
      },
    });
  }, []);

  const handleSaveNote = useCallback(async values => {
    try {
      if (values._id || values.uid) {
        // Edit existing note
        const _id = values._id || values.uid;
        const updated = await sticksService.save({
          _id,
          title: values.title,
          content: values.content,
          color: values.color,
          tags: values.tags,
        });

        if (updated) {
          setNotes(prev =>
            prev.map(n =>
              (n._id || n.uid) === _id ? { ...n, ...updated } : n
            )
          );
        }
      } else {
        // Add new note
        const created = await sticksService.save({
          title: values.title,
          content: values.content || '',
          color: values.color || '#fff9c4',
          tags: values.tags || [],
          order: 0,
          stickType: 'note',
        });

        if (created) {
          setNotes(prev => [created, ...prev]);
        }
      }
    } catch {
      // ignore save errors
    }
  }, []);

  const handleDragEnd = useCallback(event => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setNotes(prev => {
      const oldIndex = prev.findIndex(n => (n._id || n.uid) === active.id);
      const newIndex = prev.findIndex(n => (n._id || n.uid) === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;

      const reordered = arrayMove(prev, oldIndex, newIndex);

      // Persist new order to backend
      const items = reordered.map((n, idx) => ({
        _id: n._id || n.uid,
        order: idx,
      }));
      sticksService.reorder(items).catch(() => {
        // ignore reorder errors
      });

      return reordered;
    });
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const filteredNotes = filterNotes(notes, searchQuery);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '16px 0' }}>
      {/* Hover styles for sticky notes */}
      <style>{`
        .sticky-note-card:hover {
          box-shadow: 4px 8px 16px rgba(0,0,0,0.18) !important;
          transform: rotate(0deg) scale(1.02) !important;
        }
        .sticky-note-actions {
          position: absolute;
          bottom: 18px;
          left: 18px;
          display: flex;
          gap: 12px;
          opacity: 0;
          transition: opacity 0.2s;
        }
        .sticky-note-card:hover .sticky-note-actions {
          opacity: 1;
        }
        .sticky-note-ghost:hover {
          box-shadow: 4px 8px 16px rgba(0,0,0,0.14) !important;
          transform: rotate(0deg) scale(1.02) !important;
          background: rgba(0,0,0,0.05) !important;
        }
      `}</style>

      {/* Search bar (centered) */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
        <Input
          prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />}
          placeholder={translateX('search_notes')}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          allowClear
          style={{ maxWidth: 360, width: '100%' }}
        />
      </div>

      {/* Notes grid */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={filteredNotes.map(n => n._id || n.uid)} strategy={rectSortingStrategy}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 20,
              padding: '8px 4px',
            }}
          >
            {filteredNotes.length === 0 && searchQuery && (
              <div style={{ gridColumn: '1 / -1' }}>
                <Empty description={translateX('no_data')} style={{ padding: '40px 0' }} />
              </div>
            )}

            {filteredNotes.map(note => (
              <StickyNote
                key={note._id || note.uid}
                note={note}
                onEdit={handleEditNote}
                onDelete={handleDeleteNote}
              />
            ))}

            {/* Ghost sticky note (always last) */}
            {!searchQuery && (
              <Tooltip title={translateX('new_note')}>
                <div
                  className="sticky-note-ghost"
                  onClick={handleAddNote}
                  style={{
                    minHeight: 150,
                    borderRadius: 2,
                    padding: '20px 16px 12px',
                    background: 'rgba(0,0,0,0.1)',
                    transform: 'rotate(-1deg)',
                    boxShadow: '2px 4px 8px rgba(0,0,0,0.2)',
                    cursor: 'pointer',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'box-shadow 0.2s, transform 0.15s',
                  }}
                >
                  {/* Tape decoration (same as real sticky) */}
                  <div
                    style={{
                      position: 'absolute',
                      top: -8,
                      left: '50%',
                      transform: 'translateX(-50%) rotate(1deg)',
                      width: 50,
                      height: 20,
                      background: 'rgba(255,255,200,0.8)',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                      borderRadius: 2,
                      pointerEvents: 'none',
                    }}
                  />
                  <PlusOutlined style={{ fontSize: 36, color: token.colorPrimary }} />
                </div>
              </Tooltip>
            )}
          </div>
        </SortableContext>
      </DndContext>

      {/* Add/Edit Modal */}
      <StickyNoteModal
        show={showModal}
        setShow={setShowModal}
        note={editingNote}
        onSave={handleSaveNote}
      />
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default StickyBoard;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function filterNotes(notes, query) {
  if (!query || !query.trim()) return notes;
  const q = query.toLowerCase().trim();
  return notes.filter(
    n =>
      (n.title || '').toLowerCase().includes(q) ||
      (n.content || '').toLowerCase().includes(q) ||
      (n.tags || []).some(t => t.toLowerCase().includes(q))
  );
}
